import { useState } from 'react';
import { motion } from 'framer-motion';
import ScrollReveal from '@/components/react-bits/scroll-reveal';

// ── Interlocking hex grid background ──────────────────────────────────────────
const LIT_KEYS = new Set(['3-1','7-3','11-1','5-4','9-2','2-5','13-3','6-1','14-0','1-3','10-4']);

function HexGrid() {
  const R = 52;
  const h = Math.sqrt(3) * R;
  const colStep = R * 1.5;
  const rowStep = h;
  const cols = 18;
  const rows = 8;

  const hexPath = (cx: number, cy: number) =>
    `M${cx+R},${cy} L${cx+R/2},${cy+h/2} L${cx-R/2},${cy+h/2} L${cx-R},${cy} L${cx-R/2},${cy-h/2} L${cx+R/2},${cy-h/2} Z`;

  const hexes: { cx: number; cy: number; isLit: boolean; key: string }[] = [];
  for (let col = 0; col < cols; col++) {
    for (let row = 0; row < rows; row++) {
      const cx = col * colStep + R;
      const cy = row * rowStep + (col % 2 === 1 ? h / 2 : 0) + h / 2;
      const key = `${col}-${row}`;
      hexes.push({ cx, cy, isLit: LIT_KEYS.has(key), key });
    }
  }

  return (
    <svg
      className="absolute inset-0 w-full h-full pointer-events-none"
      preserveAspectRatio="xMidYMid slice"
    >
      <defs>
        <filter id="hex-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="6" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <radialGradient id="lit-fill" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#D4AF37" stopOpacity="0.08" />
          <stop offset="100%" stopColor="#D4AF37" stopOpacity="0" />
        </radialGradient>
      </defs>
      {hexes.filter(h => !h.isLit).map(({ cx, cy, key }) => (
        <path key={key} d={hexPath(cx, cy)} fill="none" stroke="#D4AF37" strokeWidth={0.75} strokeOpacity={0.07} />
      ))}
      {hexes.filter(h => h.isLit).map(({ cx, cy, key }) => (
        <g key={key} filter="url(#hex-glow)">
          <path d={hexPath(cx, cy)} fill="url(#lit-fill)" stroke="#D4AF37" strokeWidth={1.2} strokeOpacity={0.38} />
        </g>
      ))}
    </svg>
  );
}

// ── Honey drip separator — drips now overflow freely ──────────────────────────
const DRIPS = [
  { x: 80,   w: 3, h: 38, drop: 7  },
  { x: 210,  w: 5, h: 52, drop: 10 },
  { x: 390,  w: 3, h: 30, drop: 6  },
  { x: 560,  w: 6, h: 60, drop: 12 },
  { x: 720,  w: 4, h: 44, drop: 9  },
  { x: 890,  w: 5, h: 55, drop: 11 },
  { x: 1050, w: 3, h: 32, drop: 7  },
  { x: 1220, w: 6, h: 50, drop: 10 },
  { x: 1380, w: 3, h: 36, drop: 7  },
];

const WAVE_H = 90; // tall wave shelf
const MAX_DRIP = Math.max(...DRIPS.map(d => d.h + d.drop * 2));
const SEP_H = WAVE_H + MAX_DRIP + 12;

function HoneySeparator() {
  return (
    <div className="relative w-full" style={{ height: SEP_H }}>
      <svg
        viewBox={`0 0 1440 ${SEP_H}`}
        preserveAspectRatio="none"
        className="absolute inset-0 w-full h-full"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="sep-fade" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor="#9A7B20" stopOpacity="0.45" />
            <stop offset="60%"  stopColor="#7A5E18" stopOpacity="0.25" />
            <stop offset="100%" stopColor="#6B4C0A" stopOpacity="0" />
          </linearGradient>
          <filter id="drip-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
        </defs>

        {/* Big wavy honey shelf — tall amplitude, organic shape */}
        <path
          d={`M0,55 Q90,${WAVE_H} 200,48 Q310,10 430,62 Q540,${WAVE_H} 660,44 Q770,8 880,60 Q990,${WAVE_H} 1110,46 Q1230,10 1340,58 Q1400,${WAVE_H-10} 1440,52 L1440,0 L0,0 Z`}
          fill="url(#sep-fade)"
        />
        {/* Edge line following the wave */}
        <path
          d={`M0,55 Q90,${WAVE_H} 200,48 Q310,10 430,62 Q540,${WAVE_H} 660,44 Q770,8 880,60 Q990,${WAVE_H} 1110,46 Q1230,10 1340,58 Q1400,${WAVE_H-10} 1440,52`}
          fill="none"
          stroke="#9A7B20"
          strokeWidth="1.5"
          strokeOpacity="0.4"
          filter="url(#drip-glow)"
        />

        {/* Drips hang from wave bottom ~WAVE_H */}
        {DRIPS.map((d, i) => {
          const baseY = WAVE_H - 4;
          return (
            <g key={i} filter="url(#drip-glow)">
              <rect
                x={d.x - d.w / 2} y={baseY}
                width={d.w} height={d.h}
                rx={d.w / 2}
                fill="#7A5E18" fillOpacity={0.5}
              />
              <ellipse
                cx={d.x} cy={baseY + d.h + d.drop * 0.55}
                rx={d.w + d.drop * 0.55} ry={d.drop}
                fill="#8B6914" fillOpacity={0.55}
              />
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Glassmorphism feature card ────────────────────────────────────────────────
interface CardProps { icon: string; title: string; body: string; delay: number; }

function FeatureCard({ icon, title, body, delay }: CardProps) {
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [hov, setHov] = useState(false);

  return (
    <ScrollReveal delay={delay}>
      <motion.div
        className="relative overflow-hidden h-full"
        style={{
          background: 'linear-gradient(135deg, rgba(212,175,55,0.07) 0%, rgba(212,175,55,0.02) 50%, rgba(255,160,0,0.05) 100%)',
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
          border: '1px solid rgba(212,175,55,0.18)',
          borderRadius: 16,
          padding: 'clamp(1.4rem, 5vw, 2.25rem) clamp(1.15rem, 4vw, 2rem)',
          boxShadow: '0 4px 32px rgba(0,0,0,0.45), inset 0 1px 0 rgba(212,175,55,0.12)',
        }}
        whileHover={{ y: -4, boxShadow: '0 12px 48px rgba(0,0,0,0.5), 0 0 32px rgba(212,175,55,0.1), inset 0 1px 0 rgba(212,175,55,0.2)' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        onMouseMove={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos({ x: e.clientX - r.left, y: e.clientY - r.top });
        }}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
      >
        {/* Mouse spotlight */}
        <div
          className="pointer-events-none absolute transition-opacity duration-300"
          style={{
            width: 360, height: 360,
            left: pos.x - 180, top: pos.y - 180,
            background: 'radial-gradient(circle, rgba(212,175,55,0.1) 0%, transparent 65%)',
            opacity: hov ? 1 : 0,
            borderRadius: '50%',
          }}
        />

        {/* Top shimmer line on hover */}
        <motion.div
          className="absolute top-0 left-0 right-0 rounded-t-2xl"
          style={{ height: 1 }}
          animate={{ background: hov ? 'linear-gradient(90deg,transparent,rgba(212,175,55,0.7),transparent)' : 'transparent' }}
          transition={{ duration: 0.3 }}
        />

        {/* Corner accent */}
        <div className="absolute top-0 right-0 w-16 h-16 pointer-events-none" style={{
          background: 'radial-gradient(circle at top right, rgba(212,175,55,0.12) 0%, transparent 70%)',
          borderRadius: '0 16px 0 0',
        }} />

        <div className="relative z-10">
          {/* Icon in a subtle gold ring */}
          <div
            className="flex items-center justify-center mb-6"
            style={{
              width: 60, height: 60,
              borderRadius: '50%',
              background: 'rgba(212,175,55,0.08)',
              border: '1px solid rgba(212,175,55,0.22)',
              fontSize: 26,
              color: '#D4AF37',
              boxShadow: '0 0 20px rgba(212,175,55,0.12)',
              lineHeight: 1,
            }}
          >
            {icon}
          </div>

          <h3
            className="font-bold mb-3"
            style={{
              color: '#D4AF37',
              fontSize: '1.15rem',
              letterSpacing: '-0.01em',
              textShadow: '0 0 20px rgba(212,175,55,0.3)',
            }}
          >
            {title}
          </h3>
          <p className="text-body leading-relaxed" style={{ color: 'rgba(255,255,255,0.48)' }}>
            {body}
          </p>
        </div>
      </motion.div>
    </ScrollReveal>
  );
}

const FEATURES: CardProps[] = [
  { icon: '◈', title: "Honey Badger Don't Care",   body: "Bear markets, FUD, rugs, crashes — Honey Badger has seen it all and simply does not care. The most resilient creature in crypto.", delay: 0 },
  { icon: '◆', title: '0% Tax. 100% DGAF.',         body: "No buy tax. No sell tax. No dev wallet draining you. Just raw, unfiltered badger energy with every transaction.", delay: 0.1 },
  { icon: '⬡', title: 'Eats Cobras for Breakfast',  body: "Honey Badger fights off lions, survives cobra venom, and raids beehives solo. Your FUD? Absolute nothing.", delay: 0.2 },
];

// ── Section ───────────────────────────────────────────────────────────────────
export default function About() {
  return (
    <>
      <HoneySeparator />

      <section id="about" className="relative section overflow-hidden">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 pointer-events-none"
          style={{
            width: 'min(680px, 82vw)',
            height: 8,
            background: 'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.55) 50%, transparent 100%)',
            clipPath: 'polygon(18px 0%, calc(100% - 18px) 0%, 100% 100%, 0% 100%)',
          }}
        />
        <HexGrid />

        <div
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
          style={{
            width: '80vw', height: '80vh',
            background: 'radial-gradient(ellipse at center, rgba(212,175,55,0.03) 0%, transparent 70%)',
          }}
        />

        <div className="section-content relative z-10">
          <ScrollReveal>
            <p className="text-label text-center mb-5" style={{ color: '#D4AF37' }}>◆ &nbsp; The Legend &nbsp; ◆</p>
          </ScrollReveal>
          <ScrollReveal delay={0.1}>
            <h2 className="text-headline font-black text-center mb-6">
              <span style={{ color: '#FFFFFF' }}>Built Different.</span><br />
              <span className="text-gold">Wired to Survive.</span>
            </h2>
          </ScrollReveal>
          <ScrollReveal delay={0.2}>
            <p className="text-body text-center max-w-2xl mx-auto mb-16" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Honey badgers take on lions. They walk off cobra venom. They raid beehives
              and get back up for more. That fearlessness is the foundation of{' '}
              <span style={{ color: '#D4AF37' }}>$BADGER</span>.
            </p>
          </ScrollReveal>

          {/* Glassmorphism cards — gap layout, no harsh borders between them */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-20">
            {FEATURES.map((f, i) => (
              <FeatureCard key={i} {...f} />
            ))}
          </div>

          <ScrollReveal delay={0.3}>
            <div
              className="text-center px-6 py-14"
              style={{ borderTop: '1px solid rgba(212,175,55,0.18)', borderBottom: '1px solid rgba(212,175,55,0.1)' }}
            >
              <p className="text-title font-medium" style={{ color: 'rgba(255,255,255,0.55)', lineHeight: 1.6 }}>
                "He doesn't care. He doesn't mind.{' '}
                <span style={{ color: '#D4AF37', fontWeight: 700 }}>He just wants the honey.</span>
                <br />That's why they call him...{' '}
                <span style={{ color: '#FFD700', fontWeight: 800 }}>The Honey Badger.</span>"
              </p>
              <p className="text-caption mt-4" style={{ color: 'rgba(255,255,255,0.3)', letterSpacing: '0.08em' }}>— The Internet, circa forever</p>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </>
  );
}
